import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Upload, Download, Trash2, Eye, Plus } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

interface BrandAsset {
  id: number;
  asset_type: string;
  name: string;
  description?: string;
  file_name: string;
  file_size?: number;
  category?: string;
  usage_notes?: string;
  is_current: boolean;
  created_at: string;
}

interface Props {
  clientId: number;
}

export default function BrandAssetsTab({ clientId }: Props) {
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState<BrandAsset | null>(null);
  const [assetForm, setAssetForm] = useState({
    name: '',
    description: '',
    usage_notes: '',
    category: '',
    is_current: true
  });

  useEffect(() => {
    loadAssets();
  }, [clientId]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/brand-assets?client_id=${clientId}`);
      setAssets(response.data.assets || []);
    } catch (error) {
      console.error('Failed to load assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (assetId: number, fileName: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/brand-assets/${assetId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download asset:', error);
      alert('Failed to download asset');
    }
  };

  const handleDelete = async (assetId: number) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/brand-assets/${assetId}`);
      await loadAssets();
    } catch (error) {
      console.error('Failed to delete asset:', error);
      alert('Failed to delete asset');
    }
  };

  const handleEdit = (asset: BrandAsset) => {
    setEditingAsset(asset);
    setAssetForm({
      name: asset.name,
      description: asset.description || '',
      usage_notes: asset.usage_notes || '',
      category: asset.category || '',
      is_current: asset.is_current
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!editingAsset) return;

    try {
      await axios.put(`${API_BASE_URL}/brand-assets/${editingAsset.id}`, assetForm);
      setShowModal(false);
      setEditingAsset(null);
      setAssetForm({
        name: '',
        description: '',
        usage_notes: '',
        category: '',
        is_current: true
      });
      await loadAssets();
    } catch (error) {
      console.error('Failed to update asset:', error);
      alert('Failed to update asset');
    }
  };

  const getAssetTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      logo: 'bg-blue-100 text-blue-700',
      style_guide: 'bg-purple-100 text-purple-700',
      template: 'bg-green-100 text-green-700',
      font: 'bg-yellow-100 text-yellow-700',
      image: 'bg-pink-100 text-pink-700',
      video: 'bg-red-100 text-red-700',
      document: 'bg-gray-100 text-gray-700',
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading assets...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Brand Assets
          </h2>
          <p className="text-sm text-gray-600 mt-1">{assets.length} asset{assets.length !== 1 ? 's' : ''} uploaded</p>
        </div>
        <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2">
          <Upload className="w-4 h-4" />
          Upload Asset
        </button>
      </div>

      {assets.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">No brand assets uploaded yet</p>
          <button className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 flex items-center gap-2 mx-auto">
            <Plus className="w-4 h-4" />
            Upload your first asset
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asset Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage Notes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {assets.map((asset) => (
                <tr key={asset.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900 text-sm">{asset.name}</div>
                    {asset.description && (
                      <div className="text-xs text-gray-500 mt-0.5">{asset.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${getAssetTypeColor(asset.asset_type)}`}>
                      {asset.asset_type.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600">{asset.file_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{formatFileSize(asset.file_size)}</div>
                  </td>
                  <td className="px-6 py-4">
                    {asset.usage_notes ? (
                      <div className="text-xs text-blue-600 italic max-w-xs">{asset.usage_notes}</div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {asset.is_current && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                        Current
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(asset)}
                      className="text-blue-600 hover:text-blue-700 mr-3"
                      title="Edit"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => window.open(`${API_BASE_URL}/brand-assets/${asset.id}/download`, '_blank')}
                      className="text-blue-600 hover:text-blue-700 mr-3"
                      title="View"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDownload(asset.id, asset.file_name)}
                      className="text-blue-600 hover:text-blue-700 mr-3"
                      title="Download"
                    >
                      Download
                    </button>
                    <button
                      onClick={() => handleDelete(asset.id)}
                      className="text-red-600 hover:text-red-700"
                      title="Delete"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Asset Modal */}
      {showModal && editingAsset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Edit Asset Metadata</h3>
            <p className="text-xs text-gray-500 mb-4">File: {editingAsset.file_name}</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asset Name *</label>
                <input
                  type="text"
                  value={assetForm.name}
                  onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Logo Primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={assetForm.description}
                  onChange={(e) => setAssetForm({ ...assetForm, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usage Notes</label>
                <textarea
                  value={assetForm.usage_notes}
                  onChange={(e) => setAssetForm({ ...assetForm, usage_notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="How and where to use this asset"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  value={assetForm.category}
                  onChange={(e) => setAssetForm({ ...assetForm, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Marketing, Social Media"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_current"
                  checked={assetForm.is_current}
                  onChange={(e) => setAssetForm({ ...assetForm, is_current: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_current" className="ml-2 text-sm text-gray-700">
                  Current/Active version
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingAsset(null);
                  setAssetForm({
                    name: '',
                    description: '',
                    usage_notes: '',
                    category: '',
                    is_current: true
                  });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}